import React, { Component } from 'react'
import { Modal, Row, Col, Table, Spin } from 'antd'
import {
    normalizeTransactionItems,
    itemDisplayValue,
    formatTransactionItemPrice,
    formatVatRate,
    formatPhone,
    cardLast4,
    statusText,
    statusColor,
    STATUS_HEX,
    lookup,
    tranmodeText,
    isBankTxn,
    txnDateTime,
    amountFull,
    PAYMENT_PLAN_TEXT,
    CARD_ISSUER_TEXT,
    CLEARING_TEXT,
    CARD_BRAND_TEXT,
    FOREIGN_TEXT
} from '@pages/Tranzila/utils'

interface TransactionDetailsModalProps {
    visible: boolean
    onClose: () => void
    txn: any
    items?: any[]
    itemsLoading?: boolean
    itemsError?: boolean
}

const ITEM_COLUMNS = [
    {
        title: 'שם מוצר',
        key: 'item_name',
        align: 'right' as 'right',
        render: (_, item) => <span style={{ wordBreak: 'break-word' }}>{itemDisplayValue(item.item_name)}</span>
    },
    {
        title: 'כמות',
        key: 'quantity',
        width: 100,
        align: 'right' as 'right',
        render: (_, item) => <span dir="ltr">{itemDisplayValue(item.quantity)}</span>
    },
    {
        title: 'סכום ללא מע"מ',
        key: 'price',
        width: 120,
        align: 'right' as 'right',
        render: (_, item) => <span dir="ltr">{formatTransactionItemPrice(item.price)}</span>
    }
]

const DASH = '—'
const val = v => (v == null || v === '' ? DASH : v)

const Section = ({ title, children }) => (
    <div style={{ background: '#fafafa', borderRadius: 6, padding: 16, height: '100%' }}>
        <div
            style={{
                fontWeight: 'bold',
                fontSize: '1rem',
                paddingBottom: '0.4rem',
                borderBottom: '2px solid #e77910',
                marginBottom: '0.3rem'
            }}
        >
            {title}
        </div>
        {children}
    </div>
)

const Field = ({ label, children }) => (
    <div
        style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '0.45rem 0.1rem',
            borderBottom: '1px solid #f0f0f0'
        }}
    >
        <span style={{ fontWeight: 600, color: '#8c8c8c', whiteSpace: 'nowrap' }}>{label}</span>
        <span style={{ textAlign: 'left', color: '#262626', wordBreak: 'break-all', paddingRight: 8 }}>{children}</span>
    </div>
)

class TransactionDetailsModal extends Component<TransactionDetailsModalProps> {
    renderItems() {
        const { items, itemsLoading, itemsError } = this.props
        const rows = normalizeTransactionItems(items)
        if (!itemsLoading && !itemsError && !rows.length) return null

        return (
            <Row gutter={24} type="flex">
                <Col xs={24} style={{ marginBottom: '1rem' }}>
                    <Section title="מוצרים/שירותים בעסקה">
                        {itemsLoading ? (
                            <div style={{ textAlign: 'center', padding: '1rem 0' }}>
                                <Spin size="small" />
                            </div>
                        ) : itemsError ? (
                            <div style={{ padding: '0.6rem 0.1rem', color: '#8c8c8c' }}>
                                לא ניתן לטעון את פירוט המוצרים
                            </div>
                        ) : (
                            <Table
                                size="small"
                                bordered
                                pagination={false}
                                columns={ITEM_COLUMNS}
                                dataSource={rows}
                                rowKey={(_, index) => String(index)}
                                style={{ background: '#fff' }}
                            />
                        )}
                    </Section>
                </Col>
            </Row>
        )
    }

    render() {
        const { visible, onClose, txn } = this.props
        if (!txn) return null

        const bank = isBankTxn(txn)
        const statusHex = STATUS_HEX[statusColor(txn.group)] || '#8c8c8c'

        return (
            <Modal
                visible={visible}
                onCancel={onClose}
                footer={null}
                width={980}
                destroyOnClose
                title={`פרטי עסקה מספר ${txn.index} במסוף ${txn.child_terminal || ''}`}
            >
                <div
                    style={{
                        background: statusHex,
                        color: '#fff',
                        fontWeight: 600,
                        borderRadius: 4,
                        padding: '0.5rem 0.8rem',
                        marginBottom: '1rem'
                    }}
                >
                    {`${val(txn.processor_response_code)} - ${statusText(txn)}`}
                </div>
                <Row gutter={24} type="flex">
                    <Col xs={24} md={8} style={{ marginBottom: '1rem' }}>
                        <Section title="פרטי עסקה">
                            <Field label="סוג עסקה">{val(tranmodeText(txn))}</Field>
                            <Field label={'סכום כולל מע"מ'}>{amountFull(txn.amount)}</Field>
                            <Field label="שעת עסקה">{val(txnDateTime(txn))}</Field>
                            <Field label="סוג תשלום">{val(lookup(PAYMENT_PLAN_TEXT, txn.payment_plan))}</Field>
                            <Field label="מספר תשלומים">{val(txn.number_of_payments)}</Field>
                            <Field label="מספר אישור">{val(txn.authorization_number)}</Field>
                            <Field label="נשלחה מכתובת IP">
                                <span dir="ltr">{val(txn.host_ip)}</span>
                            </Field>
                        </Section>
                    </Col>
                    <Col xs={24} md={8} style={{ marginBottom: '1rem' }}>
                        <Section title="פרטי אמצעי תשלום">
                            {bank ? (
                                <>
                                    <Field label="שולם באמצעות">הוראת קבע בנקאית</Field>
                                    <Field label="בנק">{val(txn.bank)}</Field>
                                    <Field label="סניף">{val(txn.bank_branch)}</Field>
                                    <Field label="חשבון">
                                        <span dir="ltr">{val(txn.bank_account)}</span>
                                    </Field>
                                    <Field label="אסמכתא">{val(txn.refnr)}</Field>
                                    <Field label="מספר תנועה">{val(txn.txnfdnumber)}</Field>
                                </>
                            ) : (
                                <>
                                    <Field label="שולם באמצעות">כרטיס אשראי</Field>
                                    <Field label="4 ספ' אחרונות">{cardLast4(txn.credit_card_token) || DASH}</Field>
                                    <Field label="תוקף">{`${val(txn.expiration_month)}/${val(
                                        txn.expiration_year
                                    )}`}</Field>
                                    <Field label={'ת"ז בעל הכרטיס'}>{val(txn.credit_card_owner_id)}</Field>
                                    <Field label="מנפיק">{val(lookup(CARD_ISSUER_TEXT, txn.card_issuer))}</Field>
                                    <Field label="מותג">{val(lookup(CARD_BRAND_TEXT, txn.card_brand))}</Field>
                                    <Field label="סוג כרטיס">{val(lookup(FOREIGN_TEXT, txn.is_foreign))}</Field>
                                    <Field label="סולק">{val(lookup(CLEARING_TEXT, txn.clearing_processor))}</Field>
                                    <Field label="תאור כרטיס">{val(txn.card_description)}</Field>
                                    <Field label="טוקן">
                                        <span dir="ltr">{val(txn.credit_card_token)}</span>
                                    </Field>
                                    <Field label={'מספר שובר שב"א'}>{val(txn.tempref)}</Field>
                                </>
                            )}
                        </Section>
                    </Col>
                    <Col xs={24} md={8} style={{ marginBottom: '1rem' }}>
                        <Section title="פרטים נוספים לעסקה">
                            <Field label="איש קשר">{val(txn.user_defined_2)}</Field>
                            <Field label="אימייל">
                                <span dir="ltr">{val(txn.user_defined_3)}</span>
                            </Field>
                            <Field label="טלפון">
                                <span dir="ltr">{formatPhone(txn.user_defined_5) || DASH}</span>
                            </Field>
                            <Field label="כתובת">{val(txn.user_defined_4)}</Field>
                            <Field label="עיר">{val(txn.user_defined_6)}</Field>
                            <Field label="תאור מוצר">{val(txn.user_defined_7)}</Field>
                            {bank ? (
                                <Field label={'מע"מ'}><span dir="ltr">{formatVatRate(txn.user_defined_9)}</span></Field>
                            ) : (
                                <Field label="מזהה חיצוני">{val(txn.user_defined_11)}</Field>
                            )}
                        </Section>
                    </Col>
                </Row>
                {this.renderItems()}
            </Modal>
        )
    }
}

export default TransactionDetailsModal
